import { useMemo } from 'react';

import { Wrench } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { TimelineItem, TimelineToolItem } from '@repo/shared-types';

const relativeTime = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}...` : text;

const formatArgs = (args: Record<string, unknown> | null): string => {
  if (!args) return '';
  try {
    return truncate(JSON.stringify(args), 60);
  } catch {
    return '';
  }
};

const formatToolName = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface ToolInfoPopoverProps {
  items: TimelineItem[];
  /** Use dark themed styling (for ChatCard). */
  dark?: boolean;
}

export const ToolInfoPopover = ({ items, dark }: ToolInfoPopoverProps) => {
  const recentTools: TimelineToolItem[] = useMemo(() => {
    const tools = items.filter((i): i is TimelineToolItem => i.kind === 'tool');
    // Items are oldest-first (reversed from API); take the last 5 = most recent.
    return tools.slice(-5).reverse();
  }, [items]);

  if (recentTools.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-150',
            dark
              ? 'text-[#54656f] hover:bg-[#d9dbdf] hover:text-[#111b21] dark:text-[#aebac1] dark:hover:bg-[#2a3942] dark:hover:text-[#e9edef]'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted',
          )}
          title="Recent tool calls"
        >
          <Wrench className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="w-80 border-[#d1d7db] bg-white p-0 dark:border-[#2a3942] dark:bg-[#202c33]"
      >
        <div className="border-b border-[#d1d7db] px-3 py-2 dark:border-[#2a3942]">
          <h4 className="text-xs font-semibold text-[#111b21] dark:text-[#e9edef]">
            Recent Tool Calls
          </h4>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {recentTools.map((tool) => (
            <div
              key={tool.id}
              className="border-b border-[#d1d7db] px-3 py-2 last:border-b-0 dark:border-[#2a3942]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-medium text-[#111b21] dark:text-[#e9edef]">
                  {formatToolName(tool.tool_name)}
                </span>
                <span className="shrink-0 text-[10px] text-[#667781] dark:text-[#8696a0]">
                  {relativeTime(tool.timestamp)}
                </span>
              </div>
              {tool.arguments && (
                <p className="mt-0.5 text-[10px] leading-tight break-all text-[#667781] dark:text-[#8696a0]">
                  {formatArgs(tool.arguments)}
                </p>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
